import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {FaSearch} from "react-icons/fa";
import {FaClockRotateLeft} from "react-icons/fa6";
import {Modal} from "../../../components/modal/modal.jsx";
import projectUtils from "../../../utils/project-utils.js";
import {useRecentActivity} from "../hooks/use-recent-activity.js";
import ReportsService from "../../reports/reports/reports-service.js";
import useSession from "../../../hooks/use-session.js";
import useDevMenu from "../hooks/use-dev-menu.js";
import {buildMenu, sessionToContext} from "../utils/menu-filter.js";
import {flattenMenu} from "../utils/menu-utils.js";
import sentryMenu from "../resources/sentry-menu.js";
import dispatchMenu from "../resources/dispatch-menu.js";

const MAX_RESULTS = 20;

const norm = (value) => projectUtils.removeAccents(value ?? "").toLowerCase().trim();

const SearchMenuModal = ({open, setOpen}) => {
    const navigate = useNavigate();
    const {data: session} = useSession();

    const devEnabled = useDevMenu();
    const recent = useRecentActivity();
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);

    const {data: reportRows = []} = useQuery({
        queryKey: ["reports"],
        queryFn: ReportsService.list,
        enabled: open,
        staleTime: 5 * 60 * 1000,
    });

    const menuIndex = useMemo(() => {
        const ctx = sessionToContext(session);

        const items = [
            ...flattenMenu(buildMenu(sentryMenu.menu, ctx, "sentry")),
            ...flattenMenu(buildMenu(dispatchMenu.menu, ctx, "dispatch"), ["CAD"]),
        ];

        const seen = new Set();
        return items.filter((item) => {
            if (seen.has(item.to)) return false;
            seen.add(item.to);
            return true;
        });
    }, [session, devEnabled]);

    const index = useMemo(() => {
        const reports = reportRows
            .filter((report) => report?.path)
            .map((report) => ({
                id: `report-${report.id}`,
                name: report.name,
                to: `/reports/${report.path}`,
                breadcrumb: ["Relatórios", report.module_name, report.report_group].filter(Boolean).join(" › "),
            }));

        return [...menuIndex, ...reports];
    }, [menuIndex, reportRows]);

    const results = useMemo(() => {
        const term = norm(query);
        if (!term) return [];

        return index
            .filter((item) => norm(`${item.name} ${item.breadcrumb ?? ""}`).includes(term))
            .slice(0, MAX_RESULTS);
    }, [query, index]);

    const visible = query ? results : recent;

    useEffect(() => {
        setActive(0);
    }, [query]);

    const onShow = () => {
        setQuery("");
        setActive(0);
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const go = (item) => {
        if (!item) return;
        setOpen(false);
        if (item.target === "_blank") {
            window.open(item.to, "_blank", "noopener");
            return;
        }
        navigate(item.to);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((prev) => Math.min(prev + 1, visible.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(visible[active]);
        }
    };

    const showRecent = !query && recent.length > 0;
    const emptyState = query ? "Nenhum resultado encontrado." : "Digite para buscar telas do sistema.";

    return (
        <Modal open={open} setOpen={setOpen} title="Pesquisar" className="w-[95%] max-w-140 my-0!" alignTop onShow={onShow}>
            <Modal.Body className="!p-0">
                <div className="flex items-center gap-2 border-b border-gray-200 px-4">
                    <FaSearch className="text-gray-400 shrink-0" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Buscar telas e relatórios..."
                        className="w-full py-3 outline-none text-[15px] text-gray-700 bg-transparent"
                    />
                </div>

                {showRecent && (
                    <p className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-400">Últimas atividades</p>
                )}

                <ul className="max-h-[50vh] overflow-y-auto pb-1">
                    {visible.length === 0 && (
                        <li className="px-4 py-6 text-center text-sm text-gray-400">{emptyState}</li>
                    )}

                    {visible.map((item, i) => (
                        <li key={item.id ?? item.to}>
                            <button
                                type="button"
                                onMouseEnter={() => setActive(i)}
                                onClick={() => go(item)}
                                className={[
                                    "w-full text-left px-4 py-2 flex items-center gap-2 cursor-pointer",
                                    i === active ? "bg-[#eef4fb]" : "hover:bg-gray-50",
                                ].join(" ")}
                            >
                                {showRecent && <FaClockRotateLeft className="text-gray-400 shrink-0" size={13} />}
                                <span className="flex flex-col min-w-0">
                                    <span className="text-[14px] text-gray-700 truncate">{item.name ?? item.title}</span>
                                    {item.breadcrumb && <span className="text-[12px] text-gray-400 truncate">{item.breadcrumb}</span>}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </Modal.Body>
        </Modal>
    );
};

export default SearchMenuModal;
